import React from "react"
import { graphql, Link } from "gatsby"

import {
  App,
  Seo,
  AppLayoutWidth,
  Card,
  SemiBdTitle5,
  SectionHeroLegacy,
  BdTitle3,
  IconHeadset,
  IconHandshake,
  IconCogRotate,
  FormHubspot,
  SemiBdTitle4,
  RegBody,
  Page,
} from "@sr/gatsby-theme-ui"
import { useMedia, useSanity } from "@sr/common"
import styled from "styled-components"
import { rem } from "polished"
import { media } from "@sr/common"

import heroBackground from "../../images/contact/ContactUs_HeroBackground.svg"
import heroBackgroundTablet from "../../images/contact/ContactUs_HeroBackground_Tablet.svg"

const ContactSmartrentPage = ({ data, pageContext, location }: any) => {
  const {
    breadcrumb: { crumbs },
  } = pageContext

  const { title } = data?.sanityPage

  const hubspotFormID = "def18e54-cab7-4d6a-a4cd-daa9349d4aa8"
  const isMobile: boolean = !useMedia(media.midUp)

  const cardData = [
    {
      icon: <IconHeadset size={isMobile ? 24 : 32} />,
      title: "I need support",
      url: "/support/",
    },
    {
      icon: <IconCogRotate size={isMobile ? 24 : 32} />,
      title: "I'd like to integrate with SmartRent",
      url: "/contact-smartrent/integration-request/",
    },
    {
      icon: (
        <IconHandshake width={isMobile ? 24 : 32} height={isMobile ? 24 : 32} />
      ),
      title: "I'd like to partner with SmartRent",
      url: "/customers/channel-partners-installers#channel-partners-form",
    },
  ]

  return (
    <App crumbs={crumbs} location={location} crumbLabel={title}>
      <Page>
        <ContactSmartrentPageStyled>
          <SectionHeroLegacy
            heights={[19, 20]}
            className="contact-smartrent__hero"
          >
            <div className="contact-smartrent__hero-inner-container">
              <BdTitle3 className="contact-smartrent__hero-header">
                How Can We Help You?
              </BdTitle3>
              <SemiBdTitle5 className="contact-smartrent__hero-subheader">
                Please choose an option below
              </SemiBdTitle5>
            </div>
          </SectionHeroLegacy>
          <AppLayoutWidth
            className="contact-smartrent__content"
            constraint="max"
          >
            <div className="contact-smartrent__content-container">
              {cardData.map((card, i) => {
                return (
                  <Link
                    to={card.url}
                    key={`support-card-${i}`}
                    className="contact-smartrent__card-link"
                  >
                    <Card className="contact-smartrent__card" radius={4}>
                      {card.icon}
                      <div className="contact-smartrent__card-text-container">
                        <SemiBdTitle5>{card.title}</SemiBdTitle5>
                      </div>
                    </Card>
                  </Link>
                )
              })}
            </div>
            <div className="contact-smartrent__form-text-container">
              <SemiBdTitle4>Request a Demo</SemiBdTitle4>
            </div>
            <div className="contact-smartrent-demo__form-container">
              <FormHubspot
                actionType="navigate"
                id={hubspotFormID}
                navigatePath={"/contact-smartrent/success/"}
              />
            </div>
          </AppLayoutWidth>
        </ContactSmartrentPageStyled>
      </Page>
    </App>
  )
}

export default ContactSmartrentPage

const ContactSmartrentPageStyled = styled.div`
  .contact-smartrent__hero {
    background: var(--color-gray900);
    color: var(--color-primary);
  }

  .contact-smartrent__hero-inner-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${rem(8)};

    @media ${media.midUp} {
      display: flex;
      justify-content: center;
      align-items: center;
      background: url(${heroBackgroundTablet}) left 18% top 50% no-repeat;
      background-size: cover;
      height: 30vh;
      width: 100%;
    }

    @media ${media.stUp} {
      background: url(${heroBackground}) no-repeat;
      background-size: cover;
      height: 20vh;
      max-width: ${rem(1440)};
    }

    .contact-smartrent__hero-subheader {
      color: var(--color-white);
    }
  }

  .contact-smartrent__hero-header {
    color: var(--color-primary-on-dark);
    font-size: ${rem(32)};
    line-height: ${rem(40)};
    text-align: center;

    @media ${media.midUp} {
      font-size: ${rem(40)};
      line-height: ${rem(48)};
    }
  }

  .contact-smartrent__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .contact-smartrent__content-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    justify-content: center;
    align-items: center;
    gap: ${rem(8)};
    margin: ${rem(16)} 0 0;

    @media ${media.midUp} {
      gap: ${rem(24)};
      margin: ${rem(72)} 0;
      width: 100%;
    }

    @media ${media.stMidUp} {
      flex-direction: row;
      max-width: ${rem(1440)};
    }

    a {
      text-decoration: none;
    }
  }

  .contact-smartrent__card-link {
    width: 100%;
    height: ${rem(72)};

    @media ${media.midUp} {
      width: 100%;
      height: ${rem(96)};
    }

    @media ${media.stMidUp} {
      width: ${rem(356)};
      height: ${rem(128)};
    }
  }

  .contact-smartrent__card {
    width: 100%;
    height: 100%;
    display: flex;
    gap: ${rem(16)};
    align-items: center;
    color: var(--color-text);
    padding: ${rem(16)};

    @media ${media.midUp} {
      padding: ${rem(32)} 0 ${rem(32)} ${rem(32)};
      gap: ${rem(24)};
    }

    @media ${media.stMidUp} {
      justify-content: flex-start;
    }

    .contact-smartrent__card-text-container {
      @media ${media.stMidUp} {
        max-width: ${rem(220)};
      }

      h5 {
        font-size: ${rem(16)};
        line-height: ${rem(24)};

        @media ${media.midUp} {
          font-size: ${rem(24)};
          line-height: ${rem(32)};
        }
      }
    }
  }

  .contact-smartrent__form-text-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${rem(8)};
    padding-top: ${rem(40)};
    text-align: center;

    @media ${media.midUp} {
      padding-top: 0;
    }

    h4 {
      font-size: ${rem(24)};
      line-height: ${rem(32)};
      color: var(--color-primary);

      @media ${media.midUp} {
        font-size: ${rem(32)};
        line-height: ${rem(40)};
      }
    }
  }

  .contact-smartrent-demo__form-container {
    min-height: 75vh;
    margin: ${rem(48)} ${rem(16)};
    padding: ${rem(16)};
    border-radius: ${rem(8)};
    background: var(--color-white);
    box-shadow: 0px 4px 8px 0px #2d323933;

    @media ${media.midUp} {
      margin: ${rem(64)};
      padding: ${rem(32)};
      width: ${rem(600)};
    }
  }

  .hubspot-form {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
  }
`

export const Head = ({ data }: any) => {
  const { imgBuilder } = useSanity()
  const sanitySeoData = data?.sanityPage?.seo
  let metaArray = []

  if (data?.sanityPage?.ogImage != undefined) {
    const src = imgBuilder(data?.sanityPage?.ogImage?.asset)
      .width(1200)
      .height(627)
      .auto("format")
      .crop("focalpoint")
      .url()

    metaArray.push({
      property: "og:image",
      content: src,
    })
  }

  return sanitySeoData ? (
    <Seo
      title={sanitySeoData.seoTools.seo_title}
      noIndex={sanitySeoData.noindex}
      canonical={sanitySeoData.canonical}
      keywords={sanitySeoData.seoTools.focus_keyword}
      description={sanitySeoData.seoTools.meta_description}
      meta={metaArray.length ? metaArray : []}
    />
  ) : null
}

export const CONTACT_SMARTRENT_PAGE_QUERY = graphql`
  query ContactSmartrentPageQuery {
    sanityPage(
      path: { current: { eq: "/contact-smartrent/" } }
      customPage: { eq: true }
    ) {
      title
      seo {
        noindex
        canonical
        seoTools {
          focus_keyword
          focus_synonyms
          meta_description
          seo_title
        }
      }
      ogImage {
        asset {
          _id
        }
      }
    }
  }
`
